import { MoreVert } from '@mui/icons-material'
import { Box, Menu, MenuItem } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  CarrierVisitDirection,
  ContainerJourneyDto,
  LinkedOrderResponseDto,
} from '@planning/app/api'
import { HoldEnforcementAlertWrapper } from '@planning/components/hold/HoldEnforcementAlertWrapper'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import { CustomAlert } from '@planning/pages/TallymanV2/Components'
import { isOrderOnTerminal } from '@planning/utils/order-utils'
import { isAfterValidityTimestamp } from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { IconButton, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { NoSpaceInExistingAllocationAlert } from '../Atoms/NoSpaceInExistingAllocationAlert'
import { DropOffAutoCompleteRenderOption } from './DropOffAutoCompleteRenderOption'

interface IProps {
  order: IInspectContainerFormData
  onEdit?: (order: IInspectContainerFormData) => void
  onRemove?: (order: IInspectContainerFormData) => void
  isPickUp?: boolean
  hideHoldEnforcement?: boolean
}

export const GateInOrderListItem = observer(
  ({ order, onEdit, onRemove, isPickUp, hideHoldEnforcement }: IProps) => {
    const {
      orderItemStore,
      vesselVisitItemStore,
      truckVisitItemStore,
      railVisitItemStore,
      gateInViewStore,
      numericReleaseItemStore,
    } = usePlanningStore()
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const numericReleaseValidityDate = order.numericReleaseId
      ? numericReleaseItemStore.elements[order.numericReleaseId]?.data.validityDate
      : null
    const showNumericReleaseValidityDateWarning =
      !!isPickUp &&
      // Temporary workaround: compare full timestamp, not calendar day.
      isAfterValidityTimestamp(gateInViewStore.truckAppointmentDate, numericReleaseValidityDate)

    useEffect(() => {
      if (!order.numericReleaseId || numericReleaseItemStore.elements[order.numericReleaseId])
        return

      numericReleaseItemStore.fetchById(order.numericReleaseId)
    }, [numericReleaseItemStore, order.numericReleaseId])

    const handleClose = () => {
      setAnchorEl(null)
    }

    const dropOffOrder = { ...order } as ContainerJourneyDto
    if (order.linkedOrderId) {
      const linkedOrder = orderItemStore.elements[order.linkedOrderId]?.data
      if (linkedOrder) {
        dropOffOrder.linkedOrder = {
          ...linkedOrder,
          carrierVisit: linkedOrder.carrierVisitId
            ? linkedOrder.carrierVisitType === CarrierType.Vessel
              ? vesselVisitItemStore.elements[linkedOrder.carrierVisitId]?.data
              : linkedOrder.carrierVisitType === CarrierType.Train
                ? railVisitItemStore.elements[linkedOrder.carrierVisitId]?.data
                : truckVisitItemStore.elements[linkedOrder.carrierVisitId]?.data
            : null,
        } as LinkedOrderResponseDto
      }
    }

    const getAllocationAlertMessage = () => {
      if (isPickUp) return <></>

      if (gateInViewStore.orderIdsWithNoAllocationSpace.includes(order.id))
        return <NoSpaceInExistingAllocationAlert />

      return <></>
    }

    const getIsNotOnTerminalMessage = () => {
      if (isPickUp && !isOrderOnTerminal(order))
        return (
          <CustomAlert
            message={t('unitNotOnTerminalYet', 'Unit not on terminal yet')}
            severity='warning'
          />
        )

      return <></>
    }

    const getIsOnTerminalMessage = () => {
      if (!isPickUp && isOrderOnTerminal(order))
        return (
          <CustomAlert
            message={t(
              'containerIsAlreadyOnTerminalContactControlRoom',
              'Container is already on terminal, contact control room',
            )}
            severity='warning'
          />
        )

      return <></>
    }

    const getNumericReleaseValidityDateMessage = () => {
      if (showNumericReleaseValidityDateWarning) {
        return (
          <CustomAlert
            message={t(
              'truckShouldArriveBeforeValidityDate',
              'Truck should arrive before the validity date.',
            )}
            severity='warning'
          />
        )
      }

      return <></>
    }

    return (
      <Box mb={theme.customSpacing.m}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ fontSize: '1rem !important' }}>
            <DropOffAutoCompleteRenderOption
              key={`order-card-${order.id}`}
              dropOffOrder={dropOffOrder}
              alertMessage={[
                getAllocationAlertMessage(),
                getIsNotOnTerminalMessage(),
                getNumericReleaseValidityDateMessage(),
                !hideHoldEnforcement ? (
                  <HoldEnforcementAlertWrapper
                    key='hold-alert'
                    order={order}
                    context={
                      gateInViewStore.isTruckAppointment && !gateInViewStore.truckVisitId
                        ? 'TruckAppointment'
                        : 'GateIn'
                    }
                    direction={
                      isPickUp ? CarrierVisitDirection.Outbound : CarrierVisitDirection.Inbound
                    }
                  />
                ) : (
                  <React.Fragment key='no-hold-alert' />
                ),
              ]}
            />
          </Box>

          <Box>
            <IconButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              {onEdit && (
                <MenuItem onClick={() => onEdit(order)}>{t('updateUnit', 'Update unit')}</MenuItem>
              )}
              <MenuItem onClick={() => onRemove!(order)}>{t('remove', 'Remove')}</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>
    )
  },
)
